import {
  ArrowRight,
  ArrowDown,
  Calendar,
  ClipboardList,
  Check,
  X,
  Building2,
  School,
  Users,
} from "lucide-react";
import SEO from "../components/SEO";
import { CALENDLY_URL, ALIGNMENT_CHECK_URL } from "../lib/site";
import { PRODUCTS } from "../lib/products";

/* -------------------------------------------------------------------------
 *  ProductPage — the shared 9-section Leadership System page template.
 *  Governed by:  Quiet Confidence Principle™, One-Idea Rule™,
 *                Dual-Path CTA Principle™, 3 Audience Rule™,
 *                Cascade Principle™, Execution Gap Framework™.
 *  All three system pages share this architecture. Copy is data-driven from
 *  lib/products.js. Sections 8 and 9 are identical on every page and defined
 *  here.
 * ------------------------------------------------------------------------- */

const IMPACT_LEVELS = [
  { key: "district", label: "District Leadership", icon: Building2 },
  { key: "school", label: "School Leadership", icon: School },
  { key: "frontline", label: "Frontline Teams", icon: Users },
];

export default function ProductPage({ slug }) {
  const data = PRODUCTS[slug];
  if (!data) return null;

  return (
    <>
      <SEO title={data.seoTitle} description={data.seoDescription} />

      {/* SECTION 1 — HERO */}
      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid={`${slug}-hero`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-24">
          <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">{data.eyebrow}</div>
          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-[#0A192F] max-w-4xl">
            {data.hero.headline}
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            {data.hero.supportingCopy}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
              data-testid={`${slug}-hero-walkthrough-btn`}
            >
              <Calendar className="w-4 h-4" strokeWidth={1.5} /> Schedule a Walkthrough
            </a>
            <a
              href={ALIGNMENT_CHECK_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-transparent text-[#0A192F] px-8 py-3.5 text-sm tracking-wide hover:bg-[#F4F1EB] transition-colors border border-[#0A192F]"
              data-testid={`${slug}-hero-ac-btn`}
            >
              <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 2 — THE LEADERSHIP REALITY */}
      <section className="py-24 bg-[#F4F1EB]" data-testid={`${slug}-reality`}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">The Leadership Reality</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight max-w-3xl">
            {data.reality.lead}
          </h2>
          <p className="mt-8 font-serif-display text-2xl sm:text-3xl text-[#52796F] leading-snug max-w-3xl">
            {data.reality.closer}
          </p>
          <ul className="mt-12 divide-y divide-[#0A192F]/10 border-t border-b border-[#0A192F]/10">
            {data.reality.bullets.map((b, i) => (
              <li
                key={b}
                className="py-5 flex items-start gap-5"
                data-testid={`${slug}-reality-${i}`}
              >
                <span className="font-serif-display text-base text-[#38BDF8] mt-0.5 w-8 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-lg text-[#1A1D20] leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 3 — THE HIDDEN EXECUTION GAP */}
      <section className="py-24 bg-[#FDFBF7]" data-testid={`${slug}-gap`}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">The Hidden Execution Gap&trade;</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight max-w-3xl">
            {data.executionGap.name}
          </h2>
          <p className="mt-8 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            {data.executionGap.copy}
          </p>
        </div>
      </section>

      {/* SECTION 4 — A BETTER LEADERSHIP SYSTEM */}
      <section className="py-24 bg-[#F4F1EB]" data-testid={`${slug}-system`}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">A Better Leadership System</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
            {data.system.name}
          </h2>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            The {data.system.name} {data.system.description}
          </p>
        </div>
      </section>

      {/* SECTION 4b — COMPOSITE SCENARIO (Evidence Principle™) */}
      {data.scenario && (
        <section className="py-20 bg-[#FDFBF7] border-y border-[#0A192F]/10" data-testid={`${slug}-scenario`}>
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="border-l-2 border-[#D4AF37] pl-6 sm:pl-8">
              <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">Composite Scenario</div>
              <p className="mt-5 font-serif-display text-2xl sm:text-3xl text-[#0A192F] leading-snug">
                {data.scenario}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 5 — HOW IT WORKS */}
      <section className="py-24 bg-[#F4F1EB]" data-testid={`${slug}-how`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">How It Works</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              A simple leadership rhythm.
            </h2>
          </div>
          <ol className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {data.howItWorks.map((item, i) => (
              <li key={item.step} className="relative">
                <div className="bg-[#FDFBF7] border border-[#0A192F]/15 px-6 py-7 h-full flex flex-col">
                  <div className="text-xs uppercase tracking-widest text-[#38BDF8]">
                    Step {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-3 font-serif-display text-xl text-[#0A192F] leading-snug">
                    {item.step}
                  </div>
                  <p className="mt-4 text-sm text-[#4A5568] leading-relaxed">{item.body}</p>
                </div>
                {i < data.howItWorks.length - 1 && (
                  <>
                    <div className="hidden lg:flex absolute top-1/2 -right-2.5 -translate-y-1/2 z-10" aria-hidden="true">
                      <ArrowRight className="w-4 h-4 text-[#38BDF8]" strokeWidth={1.5} />
                    </div>
                    <div className="lg:hidden flex justify-center py-2" aria-hidden="true">
                      <ArrowDown className="w-4 h-4 text-[#38BDF8]" strokeWidth={1.5} />
                    </div>
                  </>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SECTION 6 — WHAT CHANGES */}
      <section className="py-24 bg-[#FDFBF7]" data-testid={`${slug}-what-changes`}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">What Changes</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              What changes when the system works.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#FDFBF7] border border-[#0A192F]/10 p-8" data-testid={`${slug}-before`}>
              <div className="text-xs uppercase tracking-widest text-[#4A5568]">Before AlignED</div>
              <ul className="mt-6 space-y-4">
                {data.whatChanges.before.map((b, i) => (
                  <li key={`b-${i}`} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-[#4A5568] mt-1 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-lg text-[#1A1D20] leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#0A192F] text-[#FDFBF7] border border-[#0A192F] p-8" data-testid={`${slug}-after`}>
              <div className="text-xs uppercase tracking-widest text-[#38BDF8]">After AlignED</div>
              <ul className="mt-6 space-y-4">
                {data.whatChanges.after.map((a, i) => (
                  <li key={`a-${i}`} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#38BDF8] mt-1 flex-shrink-0" strokeWidth={1.5} />
                    <span className="font-serif-display text-lg text-[#FDFBF7]/95 leading-relaxed">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7 — IMPACT AT EVERY LEVEL */}
      <section className="py-24 bg-[#F4F1EB]" data-testid={`${slug}-impact`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Impact at Every Level</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              How the cascade shows up in real work.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {IMPACT_LEVELS.map(({ key, label, icon: Icon }) => (
              <div
                key={key}
                className="bg-[#FDFBF7] border border-[#0A192F]/10 p-7"
                data-testid={`${slug}-impact-${key}`}
              >
                <Icon className="w-6 h-6 text-[#52796F]" strokeWidth={1.5} />
                <h3 className="mt-5 font-serif-display text-xl text-[#0A192F] leading-snug">{label}</h3>
                <p className="mt-3 text-[#4A5568] leading-relaxed">{data.impact[key]}</p>
              </div>
            ))}
          </div>

          {/* District Lens callout — keeps district leaders oriented */}
          {data.districtLens && (
            <div
              className="mt-10 bg-[#0A192F] text-[#FDFBF7] border border-[#0A192F] p-8 sm:p-10"
              data-testid={`${slug}-district-lens`}
            >
              <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">At District Scale</div>
              <p className="mt-4 font-serif-display text-xl sm:text-2xl text-[#FDFBF7] leading-snug max-w-4xl">
                {data.districtLens}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* SECTION 8 — WHY ALIGNED THINKS DIFFERENTLY (shared) */}
      <section className="py-24 bg-[#0A192F] text-[#FDFBF7]" data-testid={`${slug}-why-different`}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Why AlignED Thinks Differently</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
            Attendance, behavior, and enrollment are not isolated tasks.
          </h2>
          <p className="mt-8 text-lg text-[#FDFBF7]/85 leading-relaxed max-w-3xl">
            Each is a leadership system. Each contains an Execution Gap&trade;. Each improves when leaders can see reality clearly, understand patterns, choose deliberately, execute consistently, and learn continuously.
          </p>
          <p className="mt-6 font-serif-display text-xl sm:text-2xl text-[#FDFBF7] leading-relaxed max-w-3xl">
            That is the AlignED Method&trade;.
          </p>
        </div>
      </section>

      {/* SECTION 9 — DOMAIN-SPECIFIC FINAL CTA */}
      <section className="py-24 bg-[#FDFBF7]" data-testid={`${slug}-final-cta`}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">Start the Leadership Conversation</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
            {data.finalCta.headline}
          </h2>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed max-w-2xl mx-auto">
            {data.finalCta.copy}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
              data-testid={`${slug}-final-walkthrough-btn`}
            >
              <Calendar className="w-4 h-4" strokeWidth={1.5} /> Schedule a Walkthrough
            </a>
            <a
              href={ALIGNMENT_CHECK_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-transparent text-[#0A192F] px-8 py-3.5 text-sm tracking-wide hover:bg-[#F4F1EB] transition-colors border border-[#0A192F]"
              data-testid={`${slug}-final-ac-btn`}
            >
              <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
